import {Flag, type FlagProps} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import type {ReactNode} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, type TestIdProps} from 'shared/web';

export type ConfiguratorHeroChip = {
  label: string;
  /** Optional caption shown before the label, e.g. "Drive". */
  caption?: string;
  /** Optional leading visual, e.g. a colour dot. */
  leading?: ReactNode;
};

export type ConfiguratorHeroProps = TestIdProps & {
  makeName?: string;
  modelName?: string;
  trimName?: string;
  modelYearName?: string;
  status?: Pick<FlagProps, 'label' | 'colorScheme' | 'isSubtle'>;
  chips?: ConfiguratorHeroChip[];
  /** Fallback headline when nothing is selected yet. */
  placeholder: string;
  hint?: string;
};

const Surface = styled.div`
  position: relative;
  overflow: hidden;
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.large};
  background: linear-gradient(
    135deg,
    ${({theme}) => theme.colors.palettes.blue[10][100]} 0%,
    ${({theme}) => theme.colors.general.white} 55%,
    ${({theme}) => theme.colors.palettes.neutral[20][100]} 100%
  );
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  height: ${({theme}) => theme.getSize(7)};
  padding: 0 ${({theme}) => theme.getSize(3)};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.circular};
  background-color: ${({theme}) => theme.colors.general.white};
  white-space: nowrap;
`;

const Watermark = styled.span`
  position: absolute;
  right: ${({theme}) => `calc(-1 * ${theme.getSize(4)})`};
  bottom: ${({theme}) => `calc(-1 * ${theme.getSize(10)})`};
  opacity: 0.05;
  pointer-events: none;
`;

export function ConfiguratorHero(props: ConfiguratorHeroProps) {
  const hasModel = isNotNilOrEmpty(props.modelName);
  const hasMake = isNotNilOrEmpty(props.makeName);
  const hasTrim = isNotNilOrEmpty(props.trimName);
  const hasModelYear = isNotNilOrEmpty(props.modelYearName);
  const hasChips = isNotNilOrEmpty(props.chips);

  return (
    <Surface data-testid={suffixTestId('hero', props)}>
      <Box paddingHorizontal={6} paddingVertical={5}>
        <VStack spacing={4}>
          <HStack justify="space-between" align="flex-start" spacing={4}>
            <VStack spacing={1}>
              <Show when={hasMake}>
                <Text size="xSmall" color="secondary" alternative>
                  {props.makeName?.toUpperCase()}
                </Text>
              </Show>
              <HStack align="baseline" spacing={3} wrap>
                {hasModel ? (
                  <>
                    <Heading size={1}>{props.modelName}</Heading>
                    <Show when={hasTrim}>
                      <Heading size={2} color="secondary" alternative>
                        {props.trimName}
                      </Heading>
                    </Show>
                  </>
                ) : (
                  <Heading size={2} color="secondary" alternative>
                    {props.placeholder}
                  </Heading>
                )}
                <Show when={hasModelYear}>
                  <Flag label={props.modelYearName ?? ''} colorScheme="neutral" isSubtle />
                </Show>
              </HStack>
              <Show when={isNotNilOrEmpty(props.hint)}>
                <Text size="small" color="secondary">
                  {props.hint}
                </Text>
              </Show>
            </VStack>
            <Show when={props.status}>
              <Flag
                label={props.status?.label ?? ''}
                colorScheme={props.status?.colorScheme ?? 'neutral'}
                isSubtle={props.status?.isSubtle}
              />
            </Show>
          </HStack>
          <Show when={hasChips}>
            <HStack spacing={2} wrap>
              {props.chips?.map((chip) => (
                <Pill key={`${chip.caption ?? ''}${chip.label}`}>
                  <Show when={chip.leading}>{chip.leading}</Show>
                  <Show when={chip.caption}>
                    <Text size="xSmall" color="secondary">
                      {chip.caption}
                    </Text>
                  </Show>
                  <Text size="xSmall" alternative>
                    {chip.label}
                  </Text>
                </Pill>
              ))}
            </HStack>
          </Show>
        </VStack>
      </Box>
      <Watermark aria-hidden>
        <Icon value="automotive/car" size={40} color="palettes.blue.90.100" />
      </Watermark>
    </Surface>
  );
}
